<template>
  <div ref="megaMenuRef" class="f-col overflow-hidden py-8 c-brand-2" style="min-height: 100dvh" @keydown="handleKeydown">
    <nav :aria-label="$t.primaryNavigationLabel" class="f-col grow">
      <base-tabs-root v-model="activeTab">
        <base-tabs-list class="flex gap-1 overflow-x-auto px-6 pb-1 pt-4 scrollbar-sm duration" :class="{ 'op-0 invisible': activeL2.id }">
          <base-tabs-trigger
            v-for="(l1, i) in items.children"
            :key="l1.id"
            class="ws-nowrap b @hactive:b-white b-transparent rounded-xs px-1 subtitle-4 duration"
            :class="{ 'bg-white c-brand-1': l1.id === activeL1.id }"
            :value="`${i}`"
            @click="clickL1(l1)"
          >
            {{ l1.title }}
          </base-tabs-trigger>
        </base-tabs-list>
        <div class="relative mt-4" :style="`padding-top: ${pxToRem(inset)}`">
          <transition-group name="slide">
            <base-tabs-content
              v-show="activeTab === `${i}`"
              v-for="(l1, i) in items.children"
              :key="i"
              class="slide-left absolute top-0 w-full"
              :tabindex="activeL2.id ? -1 : 0"
              :value="`${i}`"
            >
              <a class="hidden" :href="l1.target?.url">
                {{ l1.title }}
              </a>
              <ul :id="l1.id" class="px-6">
                <template v-if="l1.columns?.length">
                  <template v-for="(column, colKey) in l1.columns" :key="colKey">
                    <li v-for="(l2, l2Key) in column.children" :key="l2Key">
                      <component
                        :is="l2.target?.url ? 'a' : 'button'"
                        class="group w-full flex items-center gap-2 py-2 between"
                        :class="{ 'op-0 invisible': activeL2.id }"
                        :href="l2.target?.url"
                        @click="clickL2(l2, $event)"
                      >
                        <span
                          class="group-hover:-rotate-3"
                          :style="{ 'transition-duration': `${SLANT_TRANSITION_DURATION}ms` }"
                        >
                          <span class="body-3 underscore">
                            {{ l2.title }}
                          </span>
                        </span>
                        <vf-icon v-if="l2.children?.length" name="chevron" size="md" />
                      </component>
                      <template v-if="l2.children?.length">
                        <transition name="slide">
                          <ul v-show="activeL2.id === l2.id" class="slide-left absolute left-0 top-0 z-1 w-full px-8">
                            <li>
                              <base-button
                                v-show="activeL2.id"
                                :ref="(el) => {
                                  if (activeTab === `${i}` && activeL2.id === l2.id)
                                    backButtonRef = el as ComponentPublicInstance
                                }"
                                class="group mb-6 flex items-center gap-2 <md:w-full"
                                style="margin-top: -3.055rem"
                                @click="activeL2 = {}"
                              >
                                <vf-icon dir="left" name="chevron" />
                                <span class="sr-only">{{ $t.backTo }}</span>
                                <span class="underscore subtitle-4">{{ activeL2.title }}</span>
                              </base-button>
                            </li>
                            <li :class="{ '<lg:hidden': activeL2.hideShopAll?.sm, 'lg:hidden': activeL2.hideShopAll?.lg }">
                              <base-link
                                class="group w-full flex py-2"
                                :to="activeL2.target?.url"
                                @click="clickL2ViewAll(l2)"
                              >
                                <span
                                  class="group-hover:-rotate-3"
                                  :style="{ 'transition-duration': `${SLANT_TRANSITION_DURATION}ms` }"
                                >
                                  <span class="body-3 underscore">
                                    {{ $t.viewAll }}
                                  </span>
                                </span>
                              </base-link>
                            </li>
                            <li v-for="(l3, l3Key) in l2.children" :key="l3Key">
                              <base-link
                                class="group w-full flex py-2"
                                :rel="activeL2.target?.url === l3.target?.url ? 'noopener nofollow' : undefined"
                                :to="l3.target?.url"
                                @click="clickL3(l3)"
                              >
                                <span
                                  class="group-hover:-rotate-3"
                                  :style="{ 'transition-duration': `${SLANT_TRANSITION_DURATION}ms` }"
                                >
                                  <span class="body-3 underscore">
                                    {{ l3.title }}
                                  </span>
                                </span>
                              </base-link>
                            </li>
                          </ul>
                        </transition>
                      </template>
                    </li>
                  </template>
                </template>
              </ul>
            </base-tabs-content>
          </transition-group>
        </div>
      </base-tabs-root>
      <div
        class="my-6 f-col grow items-start justify-center"
        :class="activeL2.id ? 'invisible max-h-0 op-0' : 'mt-12 duration'"
      >
        <div class="w-full px-6">
          <hr class="mb-6 b-grey-70">
        </div>
        <ul class="f-col gap-3 px-6">
          <li v-for="({ name, to }) in $feature.megaMenuSecondaryNav" :key="name">
            <base-link
              class="@hactive:c-white c-grey-60 duration"
              :to
              @click="$gtm.push('topNav.onClickTopNavLink', `${name}`)"
            >
              <span class="sr-only">{{ name }}</span>
              <vf-icon v-bind="icons[name]" />
            </base-link>
          </li>
        </ul>
        <div class="w-full px-6">
          <hr class="mt-6 b-grey-70">
        </div>
      </div>
    </nav>
    <vf-utility-navigation
      v-if="links"
      class="body-4 mt-12 px-6"
      :class="activeL2.id ? 'invisible max-h-0 op-0' : 'duration'"
      :links
      show-loyalty
      @click="emit('close')"
      @sign-in="emit('signIn')"
    />
    <vf-button
      :aria-label="$t.close"
      class="pointer-events-auto fixed bottom-0 right-0 m-6 rounded-xs p-3 duration lg:m-8"
      data-test-id="vf-panel-close"
      style="z-index: 10001"
      variant="primary-inverse"
      @click="emit('close')"
    >
      <vf-icon name="close" size="lg" style="margin: -0.355rem" />
    </vf-button>
  </div>
</template>

<script lang="ts" setup>
import type { MegaMenuContent, MenuItem } from '#types/components/cms/mega-menu'
import type { NavigationLink } from '#types/navigation'
import type { RecursivePartial } from '#types/utils'
import { BaseButton, BaseLink } from '#components'

const { items } = defineProps<{
  items: MegaMenuContent
  links?: NavigationLink[]
}>()

const emit = defineEmits<{
  close: []
  signIn: []
}>()

const auth = useAuthStore()
const icons = {
  customs: {
    src: 'https://assets.vans.com/image/upload/v1730381153/customs.svg',
    width: 61,
    height: 20
  },
  otw: {
    src: 'https://assets.vans.eu/image/upload/v1754646030/OTW-meganav-logo.svg',
    width: 45,
    height: 16
  },
  westend: {
    src: 'https://assets.vans.eu/image/upload/v1739884938/Vans_West_End.svg',
    width: 127,
    height: 20
  }
}
const { $feature, $t, $gtm, $viewport } = useNuxtApp()

const loyalty = useLoyaltyStore()
const activeL1 = ref<RecursivePartial<MenuItem>>(items.children[0]!)
const activeL2 = ref<RecursivePartial<MenuItem>>({})
const megaMenuRef = ref<HTMLElement>()
const backButtonRef = ref<ComponentPublicInstance | null>()
const activeTab = ref('0')

const SLANT_TRANSITION_DURATION = 200

const inset = computed(() => {
  // 40 is the height of each L1 menu item
  // 41 is the height of each L2 menu item
  // +1 for the view all link in L2
  if (activeL2.value?.children?.length) {
    return (activeL2.value.children.length + 1) * 41
  }
  else {
    return (items.children[activeTab.value]?.columns?.reduce((acc, col) =>
      acc + (col?.children?.length || 0), 0) || 0) * 40
  }
})

const getClickEventLabel
  = (...args: RecursivePartial<MenuItem>[]) => args.map((mi) => mi.delocalizedTracking || mi.name).join(' | ')

const clickL1 = (l1: MenuItem) => {
  activeL1.value = l1
  activeTab.value = items.children.findIndex(({ id }) => id === l1.id).toString()
  $gtm.push('topNav.onClickMobileMenuSection', getClickEventLabel(l1))
}

const clickL2 = async (l2: MenuItem, e: MouseEvent) => {
  if (l2.children?.length) {
    // Prevent default behavior only when ctrl or cmd key is not pressed
    if (!(e.ctrlKey || e.metaKey)) {
      e.preventDefault()

      // When on mobile, the brand wants the tilted animation to happen, so we hold the transition for the time of the animaiton
      if (!$viewport.lg) await wait(SLANT_TRANSITION_DURATION)

      activeL2.value = l2

      await nextTick()
      backButtonRef.value?.$el?.focus()

      $gtm.push('topNav.onClickMobileMenuSection', getClickEventLabel(activeL1.value, l2))
    }
    else {
      $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(activeL1.value, l2))
    }
  }
  else {
    e.preventDefault()
    navigateTo(l2.target?.url)
    $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(activeL1.value, l2))
  }
}

const clickL2ViewAll = (l2: MenuItem) => {
  $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(activeL1.value, l2))
  emit('close')
}

const clickL3 = (l3: MenuItem) => {
  $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(activeL1.value, activeL2.value, l3))
  emit('close')
}

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && activeL2.value?.id) {
    activeL2.value = {}
    e.preventDefault()
  }
}

useSwipe(megaMenuRef, {
  onSwipeEnd(_, dir) {
    if (dir === 'right' && activeL2.value.id) activeL2.value = {}
  }
})

onMounted(() => {
  if (auth.loggedIn && !loyalty?.vouchers) loyalty.getVouchers()
})
</script>
